import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import { forwardRef, ReactNode, useMemo } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/molecules/Carousel";
import { Breakpoints } from "@/foundations/breakpoints/breakpoints";

interface CarouselSectionProps {
  header?: ReactNode;
  subheader?: ReactNode;
  footer?: ReactNode;
  children: ReactNode[];
  setApi: (api: EmblaCarouselType) => void;
  disableSwipe?: boolean;
}

export const CarouselSection = forwardRef<HTMLElement, CarouselSectionProps>(
  (
    { header, subheader, footer, children, setApi, disableSwipe = false },
    ref,
  ) => {
    const carouselOptions = useMemo<EmblaOptionsType>(
      () => ({
        align: "start",
        breakpoints: {
          [`(max-width: ${Breakpoints.MD - 1}px)`]: { dragFree: true },
          [`(min-width: ${Breakpoints.SM}px)`]: { slidesToScroll: 2 },
          [`(min-width: ${Breakpoints.LG}px)`]: { slidesToScroll: 3 },
        },
      }),
      [],
    );

    return (
      <section className="tw-w-full" ref={ref}>
        <div className="ps-container-fixed ps-container-lg-margin tw-overflow-hidden">
          {!!header && (
            <div data-testing-id="carousel-section-header" className="ps-row">
              <div className="col-xs-12">{header}</div>
            </div>
          )}
          {!!subheader && (
            <div
              data-testing-id="ps-carousel-section-subheader"
              className="ps-row"
            >
              <div className="col-xs-12">{subheader}</div>
            </div>
          )}
          <div className="ps-row">
            <Carousel
              className="col-xs-12 tw-overflow-visible xxl:tw-overflow-hidden"
              totalItems={children.length}
              setApi={setApi}
              opts={carouselOptions}
              disableSwipe={disableSwipe}
            >
              <CarouselContent className="ps-row tw-flex-nowrap !-tw-ml-16 sm:-tw-ml-20">
                {children.map((item, index) => (
                  <CarouselItem
                    key={index}
                    index={index}
                    className="col-xs-11 col-sm-6 col-lg-4 !tw-pr-0 !tw-pl-16 sm:!tw-pl-20 tw-w-9/10"
                  >
                    {item}
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
          {!!footer && (
            <div
              data-testing-id="ps-carousel-section-footer"
              className="ps-row"
            >
              <div className="col-xs-12">{footer}</div>
            </div>
          )}
        </div>
      </section>
    );
  },
);

CarouselSection.displayName = "CarouselSection";
